/* eslint-disable */
import envGet from '../../Support/Env/Get/EnvGet.support';
import './Auth.service.i18n';

const FEATURE_TOGGLE_AUTH_AUTO_REFRESH = envGet({ key: `REACT_APP_BORAL_FEATURE_TOGGLE_AUTH_AUTO_REFRESH` }) === `true`;

/**
 * AuthService
 * @class AuthService
 */
class AuthService {
  /**
   * The `AuthServiceAdapterContract` interface that will be used for connecting to varius auth platforms
   * @type {AuthServiceAdapterContract}
   * @memberof AuthService
   */
  authAdapter;

  /**
   * Creates an instance of `AuthService`
   * @param {AuthServiceCreateDto} dto The params required to create an instance of `AuthService`
   * @memberof AuthService
   */
  constructor({ authAdapter }) {
    this.authAdapter = authAdapter;
  }

  /**
   * Adds the tokens into the storage. Does not log in the user.
   * @param {any} res The response
   * @param {any} tokenManager The token manager
   * @returns {void} void
   * @memberof AuthService
   */
  addTokens = (res, tokenManager) => {
    return this.authAdapter.addTokens(res, tokenManager);
  };

  /**
   * Ensure that the user is logged in and that they have a valid token that has not expired
   * @returns {Headers} The Headers with the included `Authorization` header.
   * @memberof AuthService
   */
  createHeaders = () => {
    return this.authAdapter.createHeaders();
  };

  /**
   * Clears auth from storage. Does not log out the user.
   * @returns {void} void
   * @memberof AuthService
   */
  clearAuth = () => {
    return this.authAdapter.clearAuth();
  };

  /**
   * Determine if the current `auth` has the provided `role`
   * @param {AuthServiceRoles} roleToCheck The `role` to check
   * @returns {boolean} Whether or not the user has the `role`
   * @memberof AuthService
   */
  hasRole = (roleToCheck) => {
    return this.hasAnyRole([roleToCheck]);
  };

  /**
   * Determine if the current `auth` has **any** of the provided `roles`
   * @param {Array<AuthServiceRoles>} rolesToCheck The `roles` to check
   * @returns {boolean} Whether or not the user has **any** of the `roles`
   * @memberof AuthService
   */
  hasAnyRole = (rolesToCheck) => {
    if (!this.isAuthenticated()) {
      return false;
    }
    /** @type {AuthEntity} */
    const auth = this.authAdapter.getAuth();
    const { roles } = auth.accessToken;
    return rolesToCheck.some((roleToCheck) => roles.includes(roleToCheck));
  };

  /**
   * Determine if the current `auth` has **all** of the provided `roles`
   * @param {Array<AuthServiceRoles>} rolesToCheck The `roles` to check
   * @returns {boolean} Whether or not the user has **all** of the `roles`
   * @memberof AuthService
   */
  hasAllRoles = (rolesToCheck) => {
    if (!this.isAuthenticated()) {
      return false;
    }
    /** @type {AuthEntity} */
    const auth = this.authAdapter.getAuth();
    const { roles } = auth.accessToken;
    return rolesToCheck.every((roleToCheck) => roles.includes(roleToCheck));
  };

  /**
   * Determine if the user is currently authenticated
   * @returns {boolean} Whether or not the current user is authenticated or not
   * @memberof AuthService
   */
  isAuthenticated = () => {
    return this.authAdapter.isAuthenticated();
  };

  /**
   * Returns the groups
   * @returns {Array<string>} The groups
   * @memberof AuthService
   */
  getGroups = () => {
    return this.authAdapter.getGroups();
  };

  /**
   * Returns the user by id
   * @param {string} id The user id
   * @returns {Array<string>} The response typed
   * @memberof AuthService
   */
  getUser = (id) => {
    return this.authAdapter.getUser(id);
  };

  /**
   * Updates the user profile info by user id
   * @param {string} profile The user id
   * @returns {Array<string>} The response typed
   * @memberof AuthService
   */
  updateUserProfile = (profile) => {
    return this.authAdapter.updateUserProfile(profile);
  };

  /**
   * Returns the user id from auth
   * @returns {string} The user id
   * @memberof AuthService
   */
  getUserId = () => {
    return this.authAdapter.getUserId();
  };

  /**
   * Returns the response types for the auth
   * @returns {Array<string>} The response typed
   * @memberof AuthService
   */
  getResponseTypes = () => {
    return this.authAdapter.getResponseTypes();
  };

  /**
   * Returns the scopes for the auth
   * @returns {Array<string>} The scopes
   * @memberof AuthService
   */
  getScopes = () => {
    return this.authAdapter.getScopes();
  };

  /**
   * Handle login success.
   * @param {any} response The auth success response
   * @param {any} tokenManager The token manager
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthService
   */
  loginSuccess = async (response, tokenManager) => {
    return this.authAdapter.loginSuccess(response, tokenManager);
  };

  /**
   * Attempt to login a user
   * @param {AuthServiceLoginWithUsernameAndPasswordDto} dto The required params to login
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthService
   */
  loginWithUsernameAndPassword = async ({ password, username }) => {
    return this.authAdapter.loginWithUsernameAndPassword({ password, username });
  };

  /**
   * Logout a user
   * @returns {Promise<null>} The updated representation of the current user
   * @memberof AuthService
   */
  logout = async () => {
    return this.authAdapter.logout();
  };

  /**
   * Refresh auth for a user
   * @returns {Promise<AuthEntity>} The updated representation of the current user
   * @memberof AuthService
   */
  refreshAccessToken = async () => {
    if (!FEATURE_TOGGLE_AUTH_AUTO_REFRESH) {
      return;
    }
    return this.authAdapter.refreshAccessToken();
  };

  /**
   * Fire a callback when the auth presence changes
   * @param {AuthServiceStateChangedHandler} handler The `handler` to listen to auth presence changes
   * @returns {AuthServiceStateChangedUnsubscribe} The function used to unsubscribe from auth presence changes
   * @memberof AuthService
   */
  onAuthPresenceChanged = (handler) => {
    return this.authAdapter.onAuthPresenceChanged(handler);
  };

  /**
   * Fire a callback when the auth state changes
   * @param {AuthServiceStateChangedHandler} handler The `handler` to listen to auth state changes
   * @returns {AuthServiceStateChangedUnsubscribe} The function used to unsubscribe from auth state changes
   * @memberof AuthService
   */
  onAuthStateChanged = (handler) => {
    return this.authAdapter.onAuthStateChanged(handler);
  };

  /**
   * Update auth service on auth success.
   * Call this when using a 3rd party auth service with a success/fail callback
   * @param {any} response The response from the auth service
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthService
   */
  onAuthSuccess = (response) => {
    return this.authAdapter.onAuthSuccess(response);
  };
}

export default AuthService;
